/**
 * Extract typed engineering-memory artifacts from existing AMS incident.rca payloads.
 * Never fabricates — returns only what the backend already stored.
 */
import type { AmsIncident } from '../services/api';

export type RcaEntry = Record<string, unknown>;

export function rcaEntries(incident?: AmsIncident | null): RcaEntry[] {
  return Array.isArray(incident?.rca) ? (incident!.rca as RcaEntry[]) : [];
}

export function rcaByType(incident: AmsIncident | null | undefined, type: string): RcaEntry | null {
  for (const item of rcaEntries(incident)) {
    if (String(item.type ?? '') === type) return item;
  }
  return null;
}

export function rcaAllByType(incident: AmsIncident | null | undefined, type: string): RcaEntry[] {
  return rcaEntries(incident).filter((item) => String(item.type ?? '') === type);
}

export interface EngineeringArtifactSummary {
  fingerprint: RcaEntry | null;
  patternMatch: RcaEntry | null;
  reuseValidation: RcaEntry | null;
  engineeringDecision: RcaEntry | null;
  observationJob: RcaEntry | null;
  engineeringPlan: RcaEntry | null;
  typesPresent: string[];
}

export function summarizeEngineeringArtifacts(
  incident: AmsIncident | null | undefined,
): EngineeringArtifactSummary {
  const entries = rcaEntries(incident);
  const typesPresent = [
    ...new Set(entries.map((e) => String(e.type ?? '')).filter(Boolean)),
  ];
  return {
    fingerprint: rcaByType(incident, 'incident_fingerprint'),
    patternMatch: rcaByType(incident, 'pattern_match_results'),
    reuseValidation: rcaByType(incident, 'reuse_validation_reports'),
    engineeringDecision: rcaByType(incident, 'engineering_decision'),
    observationJob: rcaByType(incident, 'observation_job'),
    engineeringPlan: rcaByType(incident, 'engineering_plan'),
    typesPresent,
  };
}

export function strField(obj: unknown, key: string): string {
  if (!obj || typeof obj !== 'object') return '';
  const v = (obj as Record<string, unknown>)[key];
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

export function numField(obj: unknown, key: string): number | null {
  if (!obj || typeof obj !== 'object') return null;
  const v = (obj as Record<string, unknown>)[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))) return Number(v);
  return null;
}

export function boolField(obj: unknown, key: string): boolean | null {
  if (!obj || typeof obj !== 'object') return null;
  const v = (obj as Record<string, unknown>)[key];
  if (typeof v === 'boolean') return v;
  return null;
}

/** Map reuse stage status → visual tone */
export function stageTone(status?: string): 'ok' | 'warn' | 'critical' | 'info' | 'pending' {
  const s = (status || '').toLowerCase();
  if (s.includes('pass') || s === 'verified' || s === 'completed' || s === 'resolved') return 'ok';
  if (s.includes('fail') || s.includes('unsafe') || s.includes('regress')) return 'critical';
  if (s.includes('partial') || s.includes('warn')) return 'warn';
  if (s.includes('run')) return 'info';
  if (s.includes('pending') || s.includes('schedul') || !s) return 'pending';
  return 'info';
}

export function executionModeLabel(mode?: string): string {
  const m = (mode || '').toUpperCase();
  if (m.includes('MEMORY')) return 'MEMORY_REUSE';
  if (m.includes('NEW') || m.includes('PLANNER')) return 'NEW_ENGINEERING';
  return m || 'UNKNOWN';
}
